import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addFilter, removeFilter, setDropModalOpen } from 'redux/actions';
import { DEVICES_PAGE_TAB, LOADING_CIRCLE } from 'common/utils/constants';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import DualCalendar from 'components/shared/CalendarFilter/CalendarFilter';
import {
  ECHO_DEVICE_TYPES,
  ESX_M6,
  FILTER_DEVICE_TYPES,
  GA2_N257,
  GA2_N258,
  GA2_N260,
  PIVOT_DEVICE_TYPES
} from '../../utils';
import '../../FiltersBar.css';

/* Define new filters here */
const FilterTable = (props) => {
  const dispatch = useDispatch();
  const activeTab = useSelector((store) => store.ui.devicesPage.activeTab);
  const filterBody = useSelector((store) => {
    if (activeTab === DEVICES_PAGE_TAB.pivot) {
      return store.data.pivotFilterDictionary;
    } else if (activeTab === DEVICES_PAGE_TAB.echo) {
      return store.data.echoFilterDictionary;
    }
    return store.data.allDevicesFilterDictionary;
  });

  let overlay = <div className="FilterBars_LoadingCircle_Overlay">{LOADING_CIRCLE}</div>;
  let options;

  switch (props.filterKey) {
    case 'Connection Status':
      options = ['Online', 'Offline'];
      return (
        <div className="FiltersBar_CheckListContainer">
          {props.fetching && overlay}
          {options.map((item) => {
            let translatedItem = item === 'Online' ? 'Connected' : 'Disconnected';
            return (
              <div
                key={item}
                className="FiltersBar_CheckListContainer_Item_Div"
                onClick={() => {
                  if (filterBody.connectionState == null) {
                    dispatch(
                      addFilter(activeTab, {
                        connectionState: [translatedItem]
                      })
                    );
                  } else {
                    if (filterBody.connectionState.includes(translatedItem)) {
                      if (filterBody.connectionState.length === 1) {
                        //if this is last item, remove whole filter entry
                        dispatch(removeFilter(activeTab, 'connectionState'));
                      } else {
                        dispatch(
                          addFilter(activeTab, {
                            connectionState: filterBody.connectionState.filter((item) => item !== translatedItem)
                          })
                        );
                      }
                    } else {
                      dispatch(
                        addFilter(activeTab, {
                          connectionState: [...filterBody.connectionState, translatedItem]
                        })
                      );
                    }
                  }
                }}
              >
                <Checkbox
                  isChecked={filterBody.connectionState && filterBody.connectionState.includes(translatedItem)}
                />
                <div className="FiltersBar_CheckListContainer_Item_Text">{item}</div>
              </div>
            );
          })}
        </div>
      );
    case 'Admin State':
      options = ['Locked', 'Unlocked'];
      let translateLists = { Locked: ['lock', 'locked'], Unlocked: ['unlocked', 'idle', 'running', 'unknown'] };
      return (
        <div className="FiltersBar_CheckListContainer">
          {props.fetching && overlay}
          {options.map((option) => {
            return (
              <div
                key={option}
                className="FiltersBar_CheckListContainer_Item_Div"
                onClick={() => {
                  if (filterBody['properties.reported.adminState'] == null) {
                    dispatch(addFilter(activeTab, { 'properties.reported.adminState': translateLists[option] }));
                  } else {
                    if (filterBody['properties.reported.adminState'].includes(translateLists[option][0])) {
                      if (filterBody['properties.reported.adminState'].length === translateLists[option].length) {
                        //if this is the last translation group
                        dispatch(removeFilter(activeTab, 'properties.reported.adminState'));
                      } else {
                        dispatch(
                          addFilter(activeTab, {
                            'properties.reported.adminState': filterBody['properties.reported.adminState'].filter(
                              (item) => !translateLists[option].includes(item)
                            )
                          })
                        );
                      }
                    } else {
                      dispatch(
                        addFilter(activeTab, {
                          'properties.reported.adminState': [
                            ...filterBody['properties.reported.adminState'],
                            ...translateLists[option]
                          ]
                        })
                      );
                    }
                  }
                }}
              >
                <Checkbox
                  isChecked={
                    filterBody['properties.reported.adminState'] &&
                    filterBody['properties.reported.adminState'].includes(translateLists[option][0])
                  }
                />
                <div className="FiltersBar_CheckListContainer_Item_Text">{option}</div>
              </div>
            );
          })}
        </div>
      );
    case 'Operational State':
      options = ['Enabled', 'Disabled'];
      return (
        <div className="FiltersBar_CheckListContainer">
          {props.fetching && overlay}
          {options.map((item) => {
            let translatedItem = item === 'Enabled' ? 'enabled' : 'disabled';
            return (
              <div
                key={item}
                className="FiltersBar_CheckListContainer_Item_Div"
                onClick={() => {
                  if (filterBody['properties.reported.operationState'] == null) {
                    dispatch(
                      addFilter(activeTab, {
                        'properties.reported.operationState': [translatedItem]
                      })
                    );
                  } else {
                    if (filterBody['properties.reported.operationState'].includes(translatedItem)) {
                      if (filterBody['properties.reported.operationState'].length === 1) {
                        //if this is last item, remove whole filter entry
                        dispatch(removeFilter(activeTab, 'properties.reported.operationState'));
                      } else {
                        dispatch(
                          addFilter(activeTab, {
                            'properties.reported.operationState': filterBody[
                              'properties.reported.operationState'
                            ].filter((item) => item !== translatedItem)
                          })
                        );
                      }
                    } else {
                      dispatch(
                        addFilter(activeTab, {
                          'properties.reported.operationState': [
                            ...filterBody['properties.reported.operationState'],
                            translatedItem
                          ]
                        })
                      );
                    }
                  }
                }}
              >
                <Checkbox
                  isChecked={
                    filterBody['properties.reported.operationState'] &&
                    filterBody['properties.reported.operationState'].includes(translatedItem)
                  }
                />
                <div className="FiltersBar_CheckListContainer_Item_Text">{item}</div>
              </div>
            );
          })}
        </div>
      );
    case 'Device Type':
      options = FILTER_DEVICE_TYPES;
      const productTypeArray = filterBody['properties.reported.productType'];
      const isInFilterDictionary = (filter) => productTypeArray.includes(filter);
      const shouldRemoveFilter = (type) =>
        (productTypeArray.length === ECHO_DEVICE_TYPES.length && type === 'Echo 5G') ||
        (productTypeArray.length === PIVOT_DEVICE_TYPES.length && type === 'Pivot 5G') ||
        (productTypeArray.length === ESX_M6.length && type === 'ESX_M6') ||
        (productTypeArray.length === GA2_N257.length && type === 'GA2-N257') ||
        (productTypeArray.length === GA2_N258.length && type === 'GA2-N258') ||
        (productTypeArray.length === GA2_N260.length && type === 'GA2-N260');

      return (
        <div className="FiltersBar_CheckListContainer">
          {props.fetching && overlay}
          {options.map((item) => {
            return (
              <div
                key={item.type}
                className="FiltersBar_CheckListContainer_Item_Div"
                onClick={() => {
                  if (productTypeArray == null) {
                    dispatch(
                      addFilter(activeTab, {
                        'properties.reported.productType': item.values
                      })
                    );

                    return;
                  }
                  // un-check a type item,and remove from filter list
                  if (item.values.every(isInFilterDictionary)) {
                    // if this is last item, remove whole filter entry
                    if (shouldRemoveFilter(item.type)) {
                      dispatch(removeFilter(activeTab, 'properties.reported.productType'));
                    } else {
                      // remove item but keep filter entry
                      dispatch(
                        addFilter(activeTab, {
                          'properties.reported.productType': productTypeArray.filter((x) => !item.values.includes(x))
                        })
                      );
                    }

                    return;
                  }
                  // check item, and add to filter list
                  dispatch(
                    addFilter(activeTab, {
                      'properties.reported.productType': [...productTypeArray, ...item.values]
                    })
                  );
                }}
              >
                <Checkbox isChecked={productTypeArray && item.values.every(isInFilterDictionary)} />
                <div className="FiltersBar_CheckListContainer_Item_Text">{item.type}</div>
              </div>
            );
          })}
        </div>
      );
    case 'Provisioned On':
      return (
        <DualCalendar
          handleSubmit={props.handleDateSubmit}
          close={() => dispatch(setDropModalOpen('Provisioned On', false))}
        />
      );
    case 'Alarms':
      options = ['critical', 'major', 'minor', 'warning'];
      return (
        <div className="FiltersBar_CheckListContainer">
          {props.fetching && overlay}
          {options.map((item) => {
            return (
              <div
                key={item}
                className="FiltersBar_CheckListContainer_Item_Div"
                onClick={() => {
                  if (filterBody[`tags.fm-alarms.${item}`] == null) {
                    dispatch(
                      addFilter(activeTab, {
                        [`tags.fm-alarms.${item}`]: true
                      })
                    );
                  } else if (item === 'no alarm') {
                    dispatch(
                      addFilter(activeTab, {
                        $notset: 'tags.fm-alarms'
                      })
                    );
                  } //for example, if critical alarm is present, we want to uncheck it and remove from filter
                  else {
                    //count how many alarm filters
                    const selectedOptions = [];
                    options.forEach((option) => {
                      if (filterBody[`tags.fm-alarms.${option}`] === true) {
                        selectedOptions.push(option);
                      }
                    });

                    if (filterBody[`tags.fm-alarms.${item}`] === true) {
                      //if this is last item, remove whole filter entry
                      if (selectedOptions.length === 1) {
                        item === 'no alarm'
                          ? dispatch(removeFilter(activeTab, '$notset'))
                          : dispatch(removeFilter(activeTab, `tags.fm-alarms.${selectedOptions[0]}`));
                      } else {
                        item === 'no alarm'
                          ? dispatch(removeFilter(activeTab, '$notset'))
                          : dispatch(removeFilter(activeTab, `tags.fm-alarms.${item}`));
                        selectedOptions.forEach((option) => {
                          if (option !== item) {
                            dispatch(
                              addFilter(activeTab, {
                                [`tags.fm-alarms.${option}`]: true
                              })
                            );
                          }
                        });
                      }
                    }
                  }
                }}
              >
                <Checkbox isChecked={filterBody[`tags.fm-alarms.${item}`]} />
                <div className="FiltersBar_CheckListContainer_Item_Text">{item}</div>
              </div>
            );
          })}
        </div>
      );
    default:
      return null;
  }
};

export default FilterTable;
